'use client';

import React from 'react';
import { Check, Minus } from 'lucide-react';
import { motion } from 'framer-motion';

const GOLD_ACCENT = '#faaf40';

const tiers = [
  {
    name: 'Kinetic Core',
    price: '$199',
    label: 'MAINTENANCE',
    color: '#00e5ff',
    glow: 'rgba(0, 229, 255, 0.2)',
    commitment: '$549',
    commitmentLabel: 'Quarterly',
    features: [true, true, true, false, false, false, false, false, false]
  },
  {
    name: 'Metabolic Sculpt',
    price: '$275',
    label: 'BEST SELLER',
    color: '#a6ff00',
    glow: 'rgba(166, 255, 0, 0.2)',
    highlight: true,
    commitment: '$799',
    commitmentLabel: 'Quarterly',
    features: [true, true, true, true, true, true, false, false, false]
  },
  {
    name: 'Signature Sanctuary',
    price: '$375',
    label: 'EXECUTIVE',
    color: '#d400ff',
    glow: 'rgba(212, 0, 255, 0.2)',
    commitment: '$3,999',
    commitmentLabel: 'Annual',
    features: [true, true, true, true, true, true, true, true, true]
  }
];

const featureList = [
  'Precision EMS Sessions',
  'Bio-Data Analysis',
  'Active Recovery Protocols',
  'Advanced EMS (3x Weekly)',
  'Hybrid Sculpt: EMS + Pilates',
  'Custom Metabolic Reset Diet',
  'High-Performance (16 Sessions)',
  'Priority Concierge Booking',
  'Private Suite Access',
];

export default function PlansComparisonTable() {
  return (
    <section className="py-12 md:py-20 bg-black text-white overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        
        {/* Header Section */}
        <div className="text-center mb-10 md:mb-16">
          <motion.span 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-[9px] md:text-[10px] uppercase tracking-[0.5em] mb-3 block font-bold text-[#faaf40]"
          >
            Aura Tier Analysis
          </motion.span>
          <h2 className="text-3xl md:text-6xl font-black uppercase italic tracking-tighter leading-tight mb-4">
            COMPARE <span className="not-italic font-light text-zinc-400">SANCTUARIES</span>
          </h2>
        </div>

        <div className="relative rounded-2xl md:rounded-[40px] border border-zinc-900 bg-zinc-950/20 backdrop-blur-md overflow-hidden shadow-2xl">
          <div className="overflow-x-auto scrollbar-hide">
            
            {/* Table Layout */}
            <div className="min-w-[750px] md:min-w-full grid grid-cols-4 p-4 md:p-8">
              
              {/* 1. Features Column */}
              <div className="flex flex-col">
                {/* Header Spacer */}
                <div className="h-32 flex items-end pb-10">
                  <span className="text-[10px] tracking-[0.3em] text-zinc-600 font-black uppercase px-2">Features</span>
                </div>
                {/* Feature Names - Exact Height Match */}
                <div className="flex flex-col">
                  {featureList.map((feature, i) => (
                    <div key={i} className="h-16 flex items-center px-2 border-b border-zinc-900/50 last:border-0">
                      <span className="text-[9px] md:text-[10px] uppercase font-bold tracking-widest text-zinc-400">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
                {/* Footer Label */}
                <div className="h-24 flex items-center mt-6 px-2">
                   <span className="text-[10px] tracking-[0.3em] text-zinc-600 font-black uppercase">Commitment</span>
                </div>
              </div>

              {/* 2. Plan Columns */}
              {tiers.map((tier, idx) => (
                <div key={idx} className="flex flex-col">
                  {/* Pricing Header */}
                  <div className="h-32 text-center flex flex-col justify-end pb-8">
                    <span className="block text-[8px] md:text-[9px] tracking-[0.3em] font-black mb-1" style={{ color: tier.color }}>
                      {tier.label}
                    </span>
                    <h3 className="text-sm md:text-xl font-black uppercase tracking-tighter text-white">
                      {tier.name}
                    </h3>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-xl md:text-2xl font-light text-white">{tier.price}</span>
                      <span className="text-[8px] text-zinc-500 uppercase">/ mo</span>
                    </div>
                  </div>

                  {/* Feature Pod - Neon Box */}
                  <div 
                    className="relative rounded-2xl md:rounded-[30px] bg-black/40 border transition-all duration-700 hover:bg-white/[0.03]"
                    style={{ 
                      boxShadow: `0 0 30px ${tier.glow}, inset 0 0 15px ${tier.glow}`,
                      borderColor: `${tier.color}30` 
                    }}
                  >
                    {tier.features.map((active, i) => (
                      <div key={i} className="h-16 flex items-center justify-center border-b border-zinc-900/30 last:border-0">
                        {active ? (
                          <Check size={20} style={{ color: tier.color }} className="drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]" strokeWidth={3} />
                        ) : (
                          <Minus size={18} className="text-zinc-800 opacity-20" strokeWidth={2} />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Commitment Label */}
                  <div className="h-24 mt-6 text-center flex flex-col justify-center">
                     <p className="text-[8px] text-zinc-500 uppercase tracking-widest mb-1">{tier.commitmentLabel}</p>
                     <p className="text-base md:text-xl font-bold" style={{ color: GOLD_ACCENT }}>{tier.commitment}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Brand Footer */}
        <div className="mt-10 text-center opacity-40">
           <p className="text-[8px] md:text-[10px] uppercase tracking-[0.4em] text-white">
             Precision Science • <span style={{ color: GOLD_ACCENT }}>Aura Wellness Clubs</span> • Islamabad Sanctuary
           </p>
        </div>
      </div>
    </section>
  );
}